import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {
    CheckCircle2,
    Check,
    ExternalLink,
    AlertCircle,
    LogOut,
    Building2,
    Loader2, ZoomIn, Image as ImageIcon, X, Copy, Search
} from 'lucide-react';
import Step1 from '../../assets/images/wizard/wizard1.png'
import Step2 from '../../assets/images/wizard/wizard2.png'
import Step3 from '../../assets/images/wizard/wizard3.png'
import Step4 from '../../assets/images/wizard/wizard4.png'
import Step5 from '../../assets/images/wizard/wizard5.png'
import Step6 from '../../assets/images/wizard/wizard6.png'
import Step7 from '../../assets/images/wizard/wizard7.png'
import Step8 from '../../assets/images/wizard/wizard8.png'
import Step9 from '../../assets/images/wizard/wizard9.png'
import Step10 from '../../assets/images/wizard/wizard10.png'
import Step11 from '../../assets/images/wizard/wizard11.png'
import Step12 from '../../assets/images/wizard/wizard12.png'
import Step13 from '../../assets/images/wizard/wizard13.png'
import Step14 from '../../assets/images/wizard/wizard14.png'
import Step15 from '../../assets/images/wizard/wizard15.png'
import Step16 from '../../assets/images/wizard/wizard16.png'
import Step17 from '../../assets/images/wizard/wizard17.png'
import Step18 from '../../assets/images/wizard/wizard18.png'
import Step19 from '../../assets/images/wizard/wizard19.png'
import Step20 from '../../assets/images/wizard/wizard20.png'
import Step21 from '../../assets/images/wizard/wizard21.png'
import Step22 from '../../assets/images/wizard/wizard22.png'
import Step23 from '../../assets/images/wizard/wizard23.png'
import Step24 from '../../assets/images/wizard/wizard24.png'
import Step25 from '../../assets/images/wizard/wizard25.png'
import Step26 from '../../assets/images/wizard/wizard26.png'
import Step27 from '../../assets/images/wizard/wizard27.png'
import Step28 from '../../assets/images/wizard/wizard28.png'
import Step29 from '../../assets/images/wizard/wizard29.png'
import Step30 from '../../assets/images/wizard/wizard30.png'
import Step31 from '../../assets/images/wizard/wizard31.png'
import Step32 from '../../assets/images/wizard/wizard32.png'
import Step33 from '../../assets/images/wizard/wizard33.png'
import Step34 from '../../assets/images/wizard/wizard34.png'

import {
    completed,
    getAccountList,
    getPendingChannel,
    getProfileInfo,
    getProfileList,
    getWizardChannelInfo,
    saveChannel,
    setupChannel
} from "../../api/wizard/wizard.api";
import {
    HOMEPAGE_MODE_EXISTS, HOMEPAGE_MODE_OPTIONAL, homepageStepMode, omoplaySiteUrls,
} from "../../libs/adminWizardHosting";
import {HOSTING_OMOPLAY, hostingLabelOf, isExternalMall} from "../../constants/hosting";
import {generateHosting, getHostingInfo} from "../../api/hosting/hosting.api";
import {channelAuthorization, kakaoProfileChannelAuth, logout} from "../../api/account/account.api";

/*
  카카오 디벨로퍼스 화면에 **그대로 붙여넣는 고정 값**들.

  화면 여러 곳(STEP 6·8)에서 같은 문자열을 쓰고, 오타 하나가 곧 연동 실패라서 한 곳에 모았다.
  값을 바꿀 때는 redirect-service 의 실제 엔드포인트와 함께 확인할 것.
*/
const KAKAO_REDIRECT_URI = 'https://api.omoplay.com/kakao/auth/token';
const KAKAO_JS_SDK_DOMAIN = 'https://*.omoplay.com';
const KAKAO_WEBHOOK_ACCOUNT_STATUS = 'https://api.omoplay.com/kakao/auth/user/status';
const KAKAO_WEBHOOK_CHANNEL_FRIEND = 'https://api.omoplay.com/kakao/auth/user/friend/status';
/** 개인정보 동의항목 신청 시 '수집 사유' 칸에 적는 문구. */
const PRIVACY_COLLECT_REASON = '온라인 자사몰 운영 계획';
/** 세팅 마무리 단계에서 채널 관리자로 초대하는 계정. */
const CHANNEL_ADMIN_EMAIL = 'headless@kakao.com';

/**
 * '이미 복사한 값' 집합(2026-10-30).
 *
 * STEP 6·8 에는 복사 칸이 20개가 넘는다. 종전에는 복사 성공 표시가 1.6초 뒤 사라져서, 카카오 콘솔과
 * 이 화면을 오가며 작업하는 실무자가 **어디까지 붙여넣었는지** 화면만 보고는 알 수 없었다.
 * 한 번 복사한 칸은 파란 상자로 남겨 '한 것' 과 '할 것' 이 구분되게 한다.
 *
 * 키 = `label|value`. 위저드 본체 상태라 STEP 7 로 갔다가 돌아와도 유지되고, 다른 채널을 세팅하려
 * 화면을 다시 열면(리스트로 돌아가기 = 전체 이동) 비워진다. 복사 **성공**만 기록한다 — 실패를
 * 기록하면 붙여넣기 전까지 아무도 모른다(`CopyButton` 의 같은 원칙).
 */
export const CopiedContext = createContext({copiedKeys: new Set(), markCopied: () => {}});
/**
 * 복사 칸의 키 = **항목 번호 + 라벨 + 값**.
 *
 * 🔴 라벨·값만 쓰면 충돌한다 — 8번(REST API 키)과 10번(JavaScript 키)의 '리다이렉트 URI' 는 라벨도 값도 같다.
 * 그 상태에서 8번을 복사하면 10번도 파란색이 됐다(2026-10-30 로컬 테스트에서 발견). 번호는 `GuideItem` 이
 * `GuideItemContext` 로 내려주므로 호출부를 고치지 않아도 칸마다 갈린다.
 */
export const copiedKeyOf = (itemNum, label, value) => `${itemNum ?? ''}|${label ?? ''}|${String(value)}`;
/** 지금 렌더 중인 `GuideItem` 의 번호. `CopyField` 가 키를 만들 때 쓴다. */
export const GuideItemContext = createContext(undefined);

export default function AdminChannelWizard() {
    const [step, setStep] = useState(1);
    const [isAnimating, setIsAnimating] = useState(false);

    // ================= State Management =================

    // Step 1: 업체 선택
    const [selectedBiz, setSelectedBiz] = useState({});

    // Step 2: 비즈니스 인증
    const [bizRegNumber, setBizRegNumber] = useState('');
    const [bizInfo, setBizInfo] = useState(null);
    const [bizError, setBizError] = useState(false);
    const [isBizChecking, setIsBizChecking] = useState(false);

    // Step 3: 관리자 휴대폰 인증 (기존 스텝2)
    const [phoneNumber, setPhoneNumber] = useState('');
    const [verificationCode, setVerificationCode] = useState('');
    const [isCodeSent, setIsCodeSent] = useState(false);
    const [isPhoneVerified, setIsPhoneVerified] = useState(false);

    const [accounts, setAccounts] = useState([])
    const [selectedAccount, setSelectedAccount] = useState()

    /*
      이어받을 미완료 신청 (2026-09-01 신규, OB-27)

      고객이 오모플레이 경로로 만든 채널은 `kakao_profile_key`·`public_id` 가 둘 다 NULL 이라
      STEP 1 목록(공개 ID 매칭)에 절대 잡히지 않는다. 그래서 이 위저드는 **항상 새 채널을 만들었고**,
      고객이 입력한 객단가·스티커·로고·컨셉·사업자번호와 자동화 메시지 7건이 고아 row 에 남았다.

      남은 유일한 매칭 단서가 **사업자번호**다. STEP 2 에서 조회해 STEP 3 에 보여주고,
      **이어받을지는 관리자가 고른다**(자동 선택하지 않는다 — 계정을 잘못 고르면 타 고객사 쓰기 사고).
    */
    const [pendingChannel, setPendingChannel] = useState(null)
    /** `true` = 위 신청을 이어받는다. `false` = 새 채널을 만든다(종전 동작). */
    const [adoptPending, setAdoptPending] = useState(false)
    /** 이어받으면서 연동 방식을 '오모플레이' 로 정정할지. 관리자가 확인 팝업을 통과했을 때만 true. */
    const [overrideHosting, setOverrideHosting] = useState(false)

    // Step 5: 홈페이지 도메인
    const [domainName, setDomainName] = useState('');
    const [brandName, setBrandName] = useState('');
    const [isDomainCreated, setIsDomainCreated] = useState(false);
    /*
      STEP 4 직후 서버가 돌려준 채널 연동 방식(2026-10-30). `{channelId, hostingName, isExternalMall, omoplaySiteId}`.

      홈페이지(오모플레이 자사몰)는 **오모플레이 제공 자사몰 고객용**이다. 카페24 등 외부 몰로 신청한
      고객은 자기 몰이 홈페이지라 STEP 5 가 필요 없는데, 종전에는 연동 방식을 보지 않고 모든 채널에
      홈페이지 생성을 강제했다 — 그대로 진행하면 쓰이지 않는 `hosting_info` 고아 행·OpenAI 배너 비용이
      생기고, STEP 6 이 오모플레이 사이트 URL 을 고객사 카카오 앱 약관 칸에 넣으라고 안내한다.

      🔑 판정 근거는 서버의 `account_channel.hosting_name` 하나다(`homepageStepMode`). STEP 3 의
         `pendingChannel.hostingName` 으로 따로 유추하지 않는다 — 재세팅 경로에는 그 값이 없고,
         두 곳을 보면 어긋난다. 조회 실패 시 null → 종전처럼 필수.
    */
    const [channelInfo, setChannelInfo] = useState(null)
    /** 외부 몰 채널에서 작업자가 '홈페이지 생성 없이 다음으로' 를 눌렀다. */
    const [homepageSkipped, setHomepageSkipped] = useState(false)
    /** 외부 몰 채널에서 접힌 생성 폼을 펼쳤다(약관 호스팅을 오모플레이로 옮기는 경우 등). */
    const [showHomepageForm, setShowHomepageForm] = useState(false)

    /** 복사 완료한 칸들(`CopiedContext`). 새 Set 을 만들어야 리렌더가 일어난다. */
    const [copiedKeys, setCopiedKeys] = useState(() => new Set())
    const markCopied = useCallback((key) => {
        setCopiedKeys((prev) => (prev.has(key) ? prev : new Set(prev).add(key)))
    }, [])
    const copiedContextValue = useMemo(() => ({copiedKeys, markCopied}), [copiedKeys, markCopied])

    // Step 5: 디벨로퍼 앱 (API 키 입력)
    const [restApiKey, setRestApiKey] = useState('');
    const [jsKey, setJsKey] = useState('');

    const [businesses, setBusinesses] = useState([]);


    const [searchTerm, setSearchTerm] = useState('');

    /**
     * 사업자번호 비교는 **숫자만 남겨서** 한다.
     *
     * 저장된 값의 형식이 섞여 있다 — 2026-08-20 이후 생성된 채널은 숫자 10자리로 정규화되지만
     * 그 전에 만들어진 채널은 `123-45-67890` 형식이다. STEP 2 입력값도 `formatBizNumber` 가
     * 하이픈을 붙인다. 정규화 없이 비교하면 기존 채널이 전부 매칭되지 않는다.
     */
    const onlyDigits = (value) => String(value ?? '').replace(/\D/g, '');

    const targetBizDigits = onlyDigits(bizRegNumber);
    const isBizMatchable = targetBizDigits.length === 10;

    const isBizMatched = (acc) =>
        isBizMatchable && (acc.bizRegNumbers ?? []).some((n) => onlyDigits(n) === targetBizDigits);

    const filteredAccounts = accounts
        .filter((acc) => {
            const lowercaseSearch = searchTerm.toLowerCase();
            const matchName = acc.name.toLowerCase().includes(lowercaseSearch);
            // ID가 숫자일 경우를 대비해 String으로 변환 후 비교합니다.
            const matchId = String(acc.id).toLowerCase().includes(lowercaseSearch);
            // 사업자번호로도 찾을 수 있게 한다(하이픈 유무와 무관하게).
            const searchDigits = onlyDigits(searchTerm);
            const matchBiz = searchDigits.length > 0 &&
                (acc.bizRegNumbers ?? []).some((n) => onlyDigits(n).includes(searchDigits));

            return matchName || matchId || matchBiz;
        })
        // 사업자번호가 일치하는 계정을 위로 올린다. **자동 선택은 하지 않는다** — 오연결을 막기 위해
        // 최종 선택은 관리자가 한다. 나머지는 서버가 준 순서(생성일 역순)를 유지한다.
        .sort((a, b) => (isBizMatched(b) ? 1 : 0) - (isBizMatched(a) ? 1 : 0));

    const matchedAccountCount = isBizMatchable ? accounts.filter(isBizMatched).length : 0;

    // 부드러운 스텝 전환 애니메이션
    const prevStep = () => {
        setStep((prev) => prev - 1);
    };

    // 부드러운 스텝 전환 애니메이션
    const nextStep = () => {
        setIsAnimating(true);
        setTimeout(() => {
            setStep((prev) => prev + 1);
            setIsAnimating(false);
        }, 500);
    };

    // 링크 새 탭 열기
    const openLink = (url) => {
        window.open(url, '_blank', 'noopener,noreferrer');
    };

    const handleLogout = () => {
        logout()
    };

    // Step 2: 비즈니스 인증 확인
    const handleVerifyBiz = () => {
        setIsBizChecking(true);
        setBizError(false);

        /*
          응답이 비어 있어도 스피너가 멈추지 않게 방어한다(2026-08-21).

          종전에는 `data.company` 를 바로 읽어서, 조회 결과가 없거나(`company: null`)
          요청 자체가 실패하면 `.then` 안에서 TypeError 가 나고 **`isBizChecking` 이 true 로 남아
          '인증 확인' 버튼이 영구히 스피너로 멈췄다.** 이 화면에서 막히면 STEP 3(계정 선택)까지
          갈 수 없어 관리자가 세팅을 진행할 방법이 없다.
        */
        getProfileInfo(bizRegNumber).then((res) => {
            const company = res?.data?.company

            if (!company?.name || company.name === '-') {
                setBizError(true);
                setBizInfo(null);
            } else {
                setBizInfo(company);
            }
            setIsBizChecking(false);
        }).catch(() => {
            setBizError(true);
            setBizInfo(null);
            setIsBizChecking(false);
        })

        /*
          같은 사업자번호로 접수된 미완료 신청을 함께 조회한다(2026-09-01).

          비즈니스 인증과는 **독립적으로** 돌린다 — 인증이 실패해도(신규 고객은 구조적으로 실패할 수 있다)
          이어받을 채널 정보는 보여줘야 하고, 반대로 이 조회가 실패해도 인증 흐름을 막아선 안 된다.
          그래서 `catch` 에서 상태만 비우고 사용자에게 오류를 띄우지 않는다.
        */
        getPendingChannel(bizRegNumber).then((res) => {
            const pending = res?.data ?? null
            setPendingChannel(pending)
            /*
              기본값은 '이어받기' 다 — 이어받는 것이 고객 입력이 보존되는 쪽이다.
              단 **다른 자사몰로 신청된 채널은 자동으로 이어받지 않는다.** 그 경우는
              연동 방식을 정정하는 판단이 필요하므로 관리자가 확인 팝업을 통과해야 한다.
            */
            // 회원가입 신청 후보(채널 없음)는 서버가 선택 계정 기준으로 어차피 복사하므로 '이어받기' 로 둔다.
            // 옛 미완료 채널 후보만 외부 몰일 때 관리자 확인(`handleAdoptPending`)을 거친다.
            setAdoptPending(!!pending && (pending.channelId == null || !isExternalMall(pending.hostingName)))
            setOverrideHosting(false)
            // 그 채널의 Owner 계정을 미리 골라 둔다(관리자가 바꿀 수 있다).
            if (pending?.accountId) {
                setSelectedAccount(pending.accountId)
            }
        }).catch(() => {
            setPendingChannel(null)
            setAdoptPending(false)
            setOverrideHosting(false)
        })
    };

    /**
     * '이 신청에 이어서 세팅' 을 누를 때.
     *
     * 고객이 **다른 자사몰(카페24 등)로 신청해 둔 경우**에만 확인을 받는다.
     * 확인을 통과하면 `overrideHosting=true` 로 두고, 저장 시 `hosting_name` 을 '오모플레이' 로 정정한다.
     * 취소하면 이어받지도 않는다 — 관리자가 상황을 다시 판단해야 하는 지점이다.
     */
    const handleAdoptPending = () => {
        if (!pendingChannel) return

        /*
          🔑 정정 확인은 **채널이 있는 후보**(옛 미완료 `account_channel`)에만 묻는다(2026-10-30).

          회원가입 신청 후보(`channelId == null`)는 STEP 4 에서 서버가 새 채널을 만들고 신청 내용을
          **선택한 계정 기준으로 그대로 복사**한다(`WizardController.adoptSignupRequest`). `adoptChannelId` 가
          없으니 `hostingName` 정정값도 보내지지 않는다 — 여기서 "오모플레이로 연동하시겠습니까?" 를 물으면
          **아무 효과도 없는 질문**이고, 카페24 고객을 연동하는 작업자에게는 잘못된 질문이다.
          그 경우는 그냥 신청 계정을 선택해 둔다.
        */
        if (pendingChannel.channelId == null) {
            setAdoptPending(true)
            setOverrideHosting(false)
            if (pendingChannel.accountId) setSelectedAccount(pendingChannel.accountId)
            return
        }

        if (isExternalMall(pendingChannel.hostingName)) {
            const ok = window.confirm(
                `현재 해당 사업자번호로 다른자사몰(${pendingChannel.hostingName}) 연동 신청이 되어있습니다.\n` +
                `그래도 오모플레이로 연동하시겠습니까?`
            )
            if (!ok) {
                setAdoptPending(false)
                setOverrideHosting(false)
                return
            }
            setOverrideHosting(true)
        } else {
            setOverrideHosting(false)
        }

        setAdoptPending(true)
        if (pendingChannel.accountId) {
            setSelectedAccount(pendingChannel.accountId)
        }
    };

    // Step 3: 휴대폰 인증 시뮬레이션
    const handleSendCode = () => {
        kakaoProfileChannelAuth(selectedBiz.searchId, phoneNumber)
            .then(({data}) => {
                if (data.code !== "200") {
                    alert(data.message)
                } else {
                    setIsCodeSent(true);
                    alert("인증번호가 전송되었습니다. 알림톡을 확인해 주세요.")
                }
            })
    };

    const handleVerifyCode = () => {
        channelAuthorization(selectedBiz.searchId, phoneNumber, verificationCode)
            .then(({data}) => {
                if ((data.code !== "200")) {
                    alert(data.message)
                } else {
                    /*
                      STEP 2 에서 입력한 사업자번호를 함께 보낸다 — 다음에 같은 업체를 다룰 때
                      계정 매칭 단서가 된다. 서버는 이미 값이 있으면 덮어쓰지 않는다.

                      `adoptChannelId` 가 있으면 서버는 **새 채널을 만들지 않고 그 채널을 이어받는다**
                      (2026-09-01, OB-27). 이때 `public_id` 도 채워지므로 이후로는 STEP 1 목록에 잡힌다.
                      `hostingName` 은 관리자가 확인 팝업을 통과했을 때만 실어 보낸다 —
                      null 이면 서버가 고객이 고른 값을 그대로 보존한다.
                    */
                    const adoptChannelId = adoptPending ? pendingChannel?.channelId ?? null : null
                    const hostingNameOverride = adoptChannelId && overrideHosting ? HOSTING_OMOPLAY : null

                    saveChannel(selectedBiz.channelId, selectedBiz.name, selectedBiz.searchId, selectedBiz.publicId, data.profileKey, selectedAccount, bizRegNumber,
                        adoptChannelId, hostingNameOverride).then(({data}) => {
                        setSelectedBiz((prev) => ({...prev, channelId: data}))
                        setIsPhoneVerified(true)
                    })
                }
            })
    }

    // Step 4: 홈페이지 생성
    const handleCreateDomain = () => {
        generateHosting(domainName, brandName, bizRegNumber, selectedBiz.channelId).then(() => {
            setIsDomainCreated(true);
        }).catch(() => setIsDomainCreated(false));
    };

    const formatBizNumber = (input) => {
        // 1. 숫자만 추출
        const numbers = input.replace(/\D/g, "").slice(0, 10);

        // 2. 3-2-5 포맷 적용
        if (numbers.length <= 3) return numbers;
        if (numbers.length <= 5)
            return `${numbers.slice(0, 3)}-${numbers.slice(3)}`;

        return `${numbers.slice(0, 3)}-${numbers.slice(3, 5)}-${numbers.slice(5)}`;
    };

    /**
     * 저장된 사업자번호를 화면용으로 정리한다.
     *
     * 10자리면 3-2-5 로 보기 좋게 끊고, **그렇지 않으면 원문을 그대로 보여준다.**
     * `formatBizNumber` 는 10자리로 자르기 때문에, 오타로 자릿수가 다른 값을 넣으면
     * 잘린 값이 표시돼 관리자가 눈으로 대조할 때 오판할 수 있다.
     */
    const displayBizNumber = (value) =>
        onlyDigits(value).length === 10 ? formatBizNumber(value) : String(value ?? '');

    /*
      카카오 심사에 제출하는 링크 3종. `?view=` 값은 생성된 사이트(redirect-service `static/front/site.html`)가
      그대로 화면 이름으로 쓴다 — `signup` · `privacy` · `terms` 모두 실제 렌더 분기가 있다.

      🔴 `signupUrl` 은 2026-09-17 신규. 종전에는 회원가입 칸에 `/view/site`(브랜드 홈)가 복사돼,
         심사 첨부용 **회원가입 화면 캡처**를 찍으러 들어가면 홈 화면이 열렸다.

      홈페이지를 만들지 않은 채널(외부 몰, 2026-10-30)은 **전부 null** 이다 — 서브도메인이 비어 있을 때
      `https://.omoplay.com/...` 같은 깨진 주소가 복사 칸에 나오면 작업자가 그대로 붙여넣는다.
      STEP 6 은 null 이면 "고객사 몰 URL 을 쓰라" 는 안내로 바꾼다(`SiteUrlField`).
    */
    const siteUrls = isDomainCreated ? omoplaySiteUrls(domainName) : null;
    const generatedUrl = siteUrls?.site;
    const signupUrl = siteUrls?.signup;
    const privacyUrl = siteUrls?.privacy;
    const termsUrl = siteUrls?.terms;

    const homepageMode = homepageStepMode(channelInfo, isDomainCreated);
    /** 홈페이지 없이 다음 단계로 갈 수 있는가 = 외부 몰 채널에서 건너뛰기를 눌렀다. */
    const canLeaveHomepageStep = isDomainCreated || (homepageMode === HOMEPAGE_MODE_OPTIONAL && homepageSkipped);
    const hostingLabel = hostingLabelOf(channelInfo?.hostingName);
    /*
      외부 쇼핑몰(카페24) 연동 채널인가 — STEP 6 에서 **카페24 자동화 도구가 처리하는 항목**을 '설정 불필요' 로 잠근다(2026-10-30).

      `[VERIFIED]` 사용자 확인: 카페24 카카오싱크 세팅은 디벨로퍼 앱 생성·연결, 동의항목 심사 URL, 서비스 약관
      (`https://{카페24 ID}.cafe24.com/api/member/agreement`) 등록까지 **카페24가 자동으로** 한다. 우리가 받아야
      하는 것은 최종 연결된 앱의 **REST API 키·JavaScript 키**, 그리고 **리다이렉트 URI·웹훅 주소 등록**이다.
      ⚠️ 카페24 외 다른 몰(아임웹·식스샵…)도 같은 플래그를 받는다 — 그 몰들의 자동화 범위는 확인되지 않았다
      `[NEEDS_CONFIRMATION]`. 그래서 '설정 불필요' 문구에 '카페24' 가 아니라 `hostingLabel` 을 쓴다.
    */
    const isMallChannel = channelInfo?.isExternalMall === true;

    useEffect(() => {
        getProfileList().then(({data}) => {
            setBusinesses(data)
        })
        getAccountList().then(({data}) => {
            setAccounts(data)
        })
    }, []);

    useEffect(() => {
        if (step === 5) {
            getHostingInfo(selectedBiz.channelId).then(({data}) => {
                if (data) {
                    setDomainName(data)
                    setIsDomainCreated(true)
                }
            })
            /*
              채널 연동 방식(2026-10-30). 실패하면 null 로 두어 **종전처럼 홈페이지 필수**가 된다 —
              조회 실패로 조용히 건너뛰면 오모플레이 자사몰 고객이 약관 URL 없이 심사로 간다.
            */
            getWizardChannelInfo(selectedBiz.channelId).then((res) => {
                setChannelInfo(res?.data ?? null)
            }).catch(() => setChannelInfo(null))
        }
    }, [step]);

    return (
        <CopiedContext.Provider value={copiedContextValue}>
        <div className="bg-gray-50 h-screen flex flex-col overflow-hidden">
            <style>{`
        /* 눈이 편안한 블러 애니메이션 (3px) */
        @keyframes slideUpFade {
          0% { opacity: 0; transform: translateY(15px); filter: blur(3px); }
          100% { opacity: 1; transform: translateY(0); filter: blur(0px); }
        }
        .animate-slide-up {
          animation: slideUpFade 0.8s cubic-bezier(0.25, 0.46, 0.45, 0.94) forwards;
        }
        .fade-out {
          opacity: 0;
          transform: translateY(-10px);
          filter: blur(2px);
          transition: all 0.5s ease;
        }
        .no-scrollbar::-webkit-scrollbar { display: none; }
        .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
      `}</style>

            {/*
              화면 상하단을 꽉 채운다(2026-09-17). 종전에는 바깥 `py-10` + `min-h-screen` 조합이라
              카드가 항상 화면보다 커서 페이지 스크롤이 생겼고, 한 화면에 담기는 정보량이 그만큼 줄었다.
            */}
            <div className={`relative w-full flex-1 min-h-0 flex flex-col bg-white shadow-sm p-5 sm:p-7 ${isAnimating ? 'fade-out' : 'animate-slide-up'}`}>

                {/* 공통 로그아웃 버튼 */}
                <button
                    onClick={handleLogout}
                    className="absolute top-6 right-6 sm:top-8 sm:right-8 flex items-center gap-1.5 text-[13px] text-gray-400 hover:text-gray-600 font-medium transition-colors z-50 bg-white/80 px-2 py-1 rounded-md backdrop-blur-sm"
                >
                    <LogOut className="w-3.5 h-3.5" />
                    로그아웃
                </button>

                {/* ================= STEP 1: 업체 선택 ================= */}
                {step === 1 && (
                    <div className="flex flex-col flex-1 min-h-0">
                        <h2 className="text-2xl font-bold text-gray-900 mb-2 pr-20">채널 세팅 대상 업체를 선택해주세요</h2>
                        <p className="text-gray-500 mb-6 text-sm">신청 중인 업체만 선택하여 세팅을 진행할 수 있습니다.</p>

                        <div className="space-y-3 mb-6 flex-1 min-h-0 overflow-y-auto no-scrollbar pr-2">
                            {businesses.map((biz) => {
                                const isApplying = biz.status !== '신청 완료';
                                const isSelected = selectedBiz.publicId === biz.publicId;

                                return (
                                    <div
                                        key={biz.publicId}
                                        onClick={() => {
                                            if (biz.status === '인증 완료') {
                                                setIsPhoneVerified(true)
                                            } else if (biz.status === '심사 요청') {
                                                setIsPhoneVerified(true)
                                                setRestApiKey(biz.restApiKey)
                                                setJsKey(biz.javascriptKey)
                                            }

                                            isApplying && setSelectedBiz({
                                                publicId: biz.publicId,
                                                name: biz.name,
                                                searchId: biz.searchId,
                                                channelId: biz.channelId,
                                                status: biz.status
                                            })
                                        }}
                                        className={`p-5 rounded-2xl border transition-all flex items-center justify-between
                      ${!isApplying ? 'bg-gray-50 border-gray-100 opacity-60 cursor-not-allowed' :
                                            isSelected ? 'bg-blue-50 border-blue-500 ring-2 ring-blue-100 cursor-pointer' : 'bg-white border-gray-200 hover:border-blue-300 cursor-pointer'}
                    `}
                                    >
                                        <div>
                                            <div className="flex items-center gap-2 mb-1">
                        <span className={`text-[11px] font-bold px-2 py-0.5 rounded ${isApplying ? 'bg-blue-100 text-blue-600' : 'bg-gray-200 text-gray-500'}`}>
                          {biz.status}
                        </span>
                                                <h4 className="font-bold text-gray-900">{biz.name}</h4>
                                            </div>
                                            <div className="text-[13px] text-gray-500 flex items-center gap-3">
                                                <span>검색 ID: <strong className="text-gray-700">{biz.searchId}</strong></span>
                                                <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                                                <span>공개 ID: <strong className="text-gray-700">{biz.publicId}</strong></span>
                                            </div>
                                        </div>
                                        {isSelected && <CheckCircle2 className="w-6 h-6 text-blue-500" />}
                                    </div>
                                );
                            })}
                        </div>

                        <button
                            onClick={nextStep}
                            disabled={!selectedBiz}
                            className="w-full bg-blue-600 text-white rounded-xl py-4 font-semibold text-[16px] disabled:bg-gray-200 disabled:text-gray-400 hover:bg-blue-700 transition-colors mt-auto"
                        >
                            다음으로
                        </button>
                    </div>
                )}

                {/* ================= STEP 2: 비즈니스 인증 ================= */}
                {step === 2 && (
                    <div className="flex flex-col flex-1 min-h-0">
                        <h2 className="text-2xl font-bold text-gray-900 mb-2 pr-20">비즈니스 인증 확인</h2>
                        <p className="text-gray-500 mb-6 text-sm">사업자 등록 번호를 입력하여 비즈니스 인증 여부를 확인합니다.</p>

                        <button
                            onClick={() => openLink(`https://business.kakao.com/${selectedBiz.publicId}/settings/review`)}
                            className="flex items-center justify-center gap-2 w-full mb-6 text-blue-600 bg-blue-50 hover:bg-blue-100 py-3 rounded-xl text-sm font-medium transition-colors"
                        >
                            <Building2 className="w-4 h-4" />
                            비즈니스 인증하러 가기
                        </button>

                        <div className="flex-1">
                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">사업자 등록 번호</label>
                            <div className="flex gap-2 mb-6">
                                <input
                                    type="text"
                                    value={bizRegNumber}
                                    onChange={(e) => setBizRegNumber(formatBizNumber(e.target.value))}
                                    placeholder="123-45-67890"
                                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3.5 outline-none focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all"
                                />
                                <button
                                    onClick={handleVerifyBiz}
                                    disabled={!bizRegNumber || isBizChecking}
                                    className="px-6 py-3.5 bg-gray-800 text-white rounded-xl font-semibold text-[14px] hover:bg-gray-900 transition-colors whitespace-nowrap disabled:bg-gray-200 disabled:text-gray-400 w-32 flex justify-center"
                                >
                                    {isBizChecking ? <Loader2 className="w-5 h-5 animate-spin" /> : '인증 확인'}
                                </button>
                            </div>

                            {bizError && (
                                <div className="bg-red-50 border border-red-100 rounded-xl p-4 flex items-center gap-2 animate-in fade-in slide-in-from-top-2">
                                    <AlertCircle className="w-5 h-5 text-red-500" />
                                    <p className="text-[14px] text-red-700 font-bold">비즈니스 인증이 되어있지 않습니다.</p>
                                </div>
                            )}

                            {(bizInfo && !bizError) && (
                                <div className="bg-white border border-gray-200 shadow-sm rounded-2xl p-5 animate-in fade-in slide-in-from-top-2">
                                    <div className="flex items-center gap-2 mb-4 border-b pb-3 border-gray-100">
                                        <CheckCircle2 className="w-5 h-5 text-blue-500" />
                                        <h4 className="font-bold text-gray-900">비즈니스 인증 완료</h4>
                                    </div>
                                    <div className="grid grid-cols-2 gap-y-3 gap-x-4 text-[13px]">
                                        <div className="text-gray-500">사업자등록번호</div><div className="text-gray-900 font-medium">{bizInfo.businessRegistrationNumber}</div>
                                        <div className="text-gray-500">사업자(상호)</div><div className="text-gray-900 font-medium">{bizInfo.name}</div>
                                        <div className="text-gray-500">대표자</div><div className="text-gray-900 font-medium">{bizInfo.representationName}</div>
                                        <div className="text-gray-500">주소</div><div className="text-gray-900 font-medium">{bizInfo.address?.baseAddress}&nbsp;{bizInfo.address?.detailAddress}</div>
                                        <div className="text-gray-500">업태</div><div className="text-gray-900 font-medium">{bizInfo.businessCategory}</div>
                                        <div className="text-gray-500">종목</div><div className="text-gray-900 font-medium">{bizInfo.businessItem}</div>
                                    </div>
                                </div>
                            )}
                        </div>

                        <button
                            onClick={nextStep}
                            disabled={!bizInfo}
                            className="w-full mt-6 bg-blue-600 text-white rounded-xl py-4 font-semibold text-[16px] disabled:bg-gray-200 disabled:text-gray-400 hover:bg-blue-700 transition-colors"
                        >
                            다음으로
                        </button>
                    </div>
                )}

                {/* ================= STEP 3: 업체 계정 확인 ================= */}
                {step === 3 && (
                    <div className="flex flex-col flex-1 min-h-0">
                        <h2 className="text-2xl font-bold text-gray-900 mb-2 pr-20">해당 채널의 계정을 선택해주세요</h2>
                        {
                            isPhoneVerified ? (
                                <h2 className="text-2xl font-bold text-gray-900 mb-2 pr-20">이미 계정이 연결되었습니다</h2>
                            ) : (
                                <>

                                    {/*
                                      이어받을 미완료 신청 (2026-09-01 신규, OB-27)

                                      고객이 이미 접수한 채널이 있으면 **그것을 이어서 세팅**해야 한다.
                                      새로 만들면 아래 표시된 값(객단가·스티커·로고·컨셉)과 자동화 메시지 7건이
                                      전부 고아 row 에 남고, 고객이 재진입하면 그 고아 row 로 되돌아간다(OB-28).

                                      **자동으로 이어받지 않는다** — 다른 자사몰로 신청된 채널은
                                      관리자가 확인 팝업을 통과해야 이어받고, 그때 연동 방식을 정정한다.
                                    */}
                                    {pendingChannel && (
                                        <div className={`mb-4 rounded-xl border p-4 ${
                                            adoptPending ? 'bg-emerald-50 border-emerald-200' : 'bg-amber-50 border-amber-300'
                                        }`}>
                                            <div className="flex items-start gap-2 mb-3">
                                                {adoptPending
                                                    ? <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
                                                    : <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />}
                                                <div className="text-[13px] leading-relaxed">
                                                    <p className={`font-bold ${adoptPending ? 'text-emerald-800' : 'text-amber-800'}`}>
                                                        사업자번호 {displayBizNumber(pendingChannel.bizRegNumber)} 로 접수된 연동 신청이 있습니다.
                                                    </p>
                                                    <p className={adoptPending ? 'text-emerald-700' : 'text-amber-700'}>
                                                        {/*
                                                          회원가입 신청 후보(채널 없음)는 선택지가 없다 — STEP 4 에서 서버가 **선택한 계정** 기준으로
                                                          새 채널을 만들고 신청 내용을 복사한다(2026-10-30 문구 정정. 종전 문구 "새 채널은 빈 상태로
                                                          시작합니다" 는 옛 미완료 채널 후보에만 맞는 말이었다).
                                                        */}
                                                        {pendingChannel.channelId == null
                                                            ? '신청 계정을 선택해 두었습니다. STEP 4 에서 새 채널이 만들어지고 신청 내용(연동 방식·몰 계정·객단가·스티커·로고·컨셉)이 그 채널에 복사됩니다. 다른 계정을 고르면 복사되지 않습니다.'
                                                            : adoptPending
                                                                ? '이 신청을 이어서 세팅합니다. 고객이 입력한 내용이 그대로 유지됩니다.'
                                                                : '새 채널로 생성하면 아래 내용은 이 채널에 남고 새 채널은 빈 상태로 시작합니다.'}
                                                    </p>
                                                    {pendingChannel.channelId == null && isExternalMall(pendingChannel.hostingName) && (
                                                        <p className="text-emerald-700 mt-1">
                                                            외부 쇼핑몰({hostingLabelOf(pendingChannel.hostingName)}) 연동 신청입니다 — STEP 5 홈페이지 생성은 건너뛸 수 있습니다.
                                                        </p>
                                                    )}
                                                </div>
                                            </div>

                                            <div className="grid grid-cols-2 gap-y-1.5 gap-x-4 text-[13px] bg-white/70 rounded-lg p-3 mb-3">
                                                <div className="text-gray-500">신청 계정</div>
                                                <div className="text-gray-900 font-medium">{pendingChannel.accountName ?? '-'} ({pendingChannel.accountId ?? '-'})</div>
                                                <div className="text-gray-500">신청 일시</div>
                                                <div className="text-gray-900 font-medium">{pendingChannel.createdAt}</div>
                                                <div className="text-gray-500">연동 방식</div>
                                                <div className={`font-medium ${isExternalMall(pendingChannel.hostingName) ? 'text-amber-700' : 'text-gray-900'}`}>
                                                    {hostingLabelOf(pendingChannel.hostingName)}
                                                    {pendingChannel.hasHostingCredential && <span className="text-gray-500"> · 몰 계정 있음</span>}
                                                </div>
                                                <div className="text-gray-500">객단가</div>
                                                <div className="text-gray-900 font-medium">{pendingChannel.customerUnitPrice?.toLocaleString() ?? 0}원</div>
                                                <div className="text-gray-500">스티커</div>
                                                <div className="text-gray-900 font-medium">{pendingChannel.stickerType ?? '-'}</div>
                                                <div className="text-gray-500">로고 / 컨셉</div>
                                                <div className="text-gray-900 font-medium">
                                                    {pendingChannel.logoUrl ? '있음' : '없음'} / {pendingChannel.concept ? '있음' : '없음'}
                                                </div>
                                            </div>

                                            {/*
                                              연동 방식이 다른 자사몰인데 이어받기로 한 경우 = 정정이 예약된 상태.
                                              무엇이 바뀌는지 저장 전에 글로 보여준다.
                                            */}
                                            {overrideHosting && (
                                                <p className="text-[13px] text-emerald-800 font-medium mb-3">
                                                    연동 방식을 <strong>{hostingLabelOf(pendingChannel.hostingName)}</strong> →
                                                    <strong> {hostingLabelOf(HOSTING_OMOPLAY)}</strong> 로 정정합니다.
                                                    (몰 계정 정보는 지우지 않습니다)
                                                </p>
                                            )}

                                            {/* 옛 미완료 채널 후보에만 선택지가 있다. 신청 후보는 `adoptChannelId` 가 없어 두 버튼의 서버 동작이 같다. */}
                                            {pendingChannel.channelId != null && (
                                            <div className="flex gap-2">
                                                <button
                                                    onClick={handleAdoptPending}
                                                    className={`flex-1 py-2.5 rounded-lg text-[13px] font-semibold transition-colors ${
                                                        adoptPending ? 'bg-emerald-600 text-white' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                                                    }`}
                                                >
                                                    이 신청에 이어서 세팅
                                                </button>
                                                <button
                                                    onClick={() => { setAdoptPending(false); setOverrideHosting(false); }}
                                                    className={`flex-1 py-2.5 rounded-lg text-[13px] font-semibold transition-colors ${
                                                        !adoptPending ? 'bg-gray-800 text-white' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                                                    }`}
                                                >
                                                    새 채널로 생성
                                                </button>
                                            </div>
                                            )}
                                        </div>
                                    )}

                                    {/*
                                      사업자번호 매칭 안내 (2026-08-21)

                                      STEP 2 에서 입력한 사업자번호로 `account_channel.biz_reg_number` →
                                      `account_permission`(Owner) → `account` 를 역추적한 결과다.
                                      서버가 계정마다 `bizRegNumbers` 를 내려주고, 여기서 숫자만 남겨 비교한다.
                                      일치 계정을 상단으로 올리기만 하고 **자동 선택은 하지 않는다.**
                                    */}
                                    {isBizMatchable && (
                                        matchedAccountCount > 0 ? (
                                            <div className="mb-4 bg-blue-50 border border-blue-100 rounded-xl p-4 flex items-start gap-2">
                                                <CheckCircle2 className="w-5 h-5 text-blue-500 shrink-0 mt-0.5" />
                                                <p className="text-[13px] text-blue-800 leading-relaxed">
                                                    사업자번호 <strong>{bizRegNumber}</strong> 로 등록된 계정 <strong>{matchedAccountCount}건</strong>을 목록 위로 올렸습니다.
                                                    <br />
                                                    <span className="text-blue-600">동명 계정이 있을 수 있으니 최종 선택은 직접 확인해 주세요.</span>
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="mb-4 bg-gray-50 border border-gray-200 rounded-xl p-4 flex items-start gap-2">
                                                <AlertCircle className="w-5 h-5 text-gray-400 shrink-0 mt-0.5" />
                                                <p className="text-[13px] text-gray-600 leading-relaxed">
                                                    사업자번호 <strong>{bizRegNumber}</strong> 와 일치하는 계정을 찾지 못했습니다.
                                                    <br />
                                                    <span className="text-gray-500">고객이 온보딩에서 사업자번호를 입력하지 않았거나 다른 번호로 입력한 경우입니다. 이름·전화번호로 확인해 주세요.</span>
                                                </p>
                                            </div>
                                        )
                                    )}

                                    {/* 중요: 검색 입력창 추가 */}
                                    <div className="relative mb-4">
                            <span className="absolute inset-y-0 left-0 flex items-center pl-4 pointer-events-none">
                                <Search className="w-5 h-5 text-gray-400" />
                            </span>
                                        <input
                                            type="text"
                                            placeholder="이름 또는 ID로 검색"
                                            value={searchTerm}
                                            onChange={(e) => setSearchTerm(e.target.value)}
                                            className="w-full pl-11 pr-4 py-3.5 bg-white border border-gray-200 rounded-xl text-[14px] focus:border-blue-500 focus:ring-2 focus:ring-blue-100 focus:outline-none transition-all placeholder-gray-400"
                                        />
                                    </div>

                                    {/* 기존 accounts 대신 filteredAccounts를 사용하여 렌더링 */}
                                    <div className="space-y-3 mb-6 flex-1 min-h-0 overflow-y-auto no-scrollbar pr-2">
                                        {filteredAccounts.length > 0 ? (
                                            filteredAccounts.map((acc) => {
                                                const isSelected = selectedAccount === acc.id;
                                                const isMatched = isBizMatched(acc);
                                                const bizNumbers = acc.bizRegNumbers ?? [];

                                                return (
                                                    <div
                                                        key={acc.id}
                                                        onClick={() => setSelectedAccount(acc.id)}
                                                        className={`p-5 rounded-2xl border transition-all flex items-center justify-between cursor-pointer ${
                                                            isSelected ? 'bg-blue-50 border-blue-500 ring-2 ring-blue-100'
                                                                : isMatched ? 'bg-amber-50/60 border-amber-300 ring-1 ring-amber-200 hover:border-amber-400'
                                                                    : 'bg-white border-gray-200 hover:border-blue-300'}`}
                                                    >
                                                        <div>
                                                            <div className="flex items-center gap-2 mb-1">
                                                                {isMatched && (
                                                                    <span className="text-[11px] font-bold px-2 py-0.5 rounded bg-amber-100 text-amber-700 whitespace-nowrap">
                                                                        사업자번호 일치
                                                                    </span>
                                                                )}
                                                                <h4 className="font-bold text-gray-900">{acc.name}</h4>
                                                            </div>
                                                            <div className="text-[13px] text-gray-500 flex items-center gap-3">
                                                                <span>ID: <strong className="text-gray-700">{acc.id}</strong></span>
                                                                <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                                                                <span>전화번호: <strong className="text-gray-700">{acc.phoneNumber}</strong></span>
                                                                <span className="w-1 h-1 rounded-full bg-gray-300"></span>
                                                                <span>생성 날짜: <strong className="text-gray-700">{acc.createdAt}</strong></span>
                                                            </div>
                                                            {/* 이 계정이 Owner 인 채널들의 사업자번호. 관리자가 눈으로 대조할 수 있게 함께 보여준다. */}
                                                            {bizNumbers.length > 0 && (
                                                                <div className="text-[13px] text-gray-500 mt-1">
                                                                    사업자번호: <strong className={isMatched ? 'text-amber-700' : 'text-gray-700'}>{bizNumbers.map(displayBizNumber).join(', ')}</strong>
                                                                </div>
                                                            )}
                                                        </div>
                                                        {isSelected && <CheckCircle2 className="w-6 h-6 text-blue-500 shrink-0" />}
                                                    </div>
                                                );
                                            })
                                        ) : (
                                            /* 검색 결과가 없을 때 보여줄 UI */
                                            <div className="text-center py-12 text-gray-400 text-[14px]">
                                                검색 결과와 일치하는 계정이 없습니다.
                                            </div>
                                        )}
                                    </div>
                                </>
                            )
                        }

                        <button
                            onClick={nextStep}
                            disabled={!selectedAccount && !isPhoneVerified}
                            className="w-full bg-blue-600 text-white rounded-xl py-4 font-semibold text-[16px] disabled:bg-gray-200 disabled:text-gray-400 hover:bg-blue-700 transition-colors mt-auto"
                        >
                            다음으로
                        </button>
                    </div>
                )}

                {/* ================= STEP 4: (관리자 본인 인증) ================= */}
                {step === 4 && (
                    <div className="flex flex-col flex-1 min-h-0">
                        <h2 className="text-2xl font-bold text-gray-900 mb-2 pr-20">관리자 본인 인증을 진행해주세요</h2>
                        <p className="text-gray-500 mb-6 text-sm">카카오 비즈메시지 등록을 위해 필수적인 단계입니다.</p>

                        {
                            isPhoneVerified
                                ? (<div className="space-y-4 flex-1 mb-6">인증되었습니다.</div>)
                                : (
                                    <div className="space-y-4 flex-1 mb-6">
                                        <div>
                                            <label className="block text-sm font-semibold text-gray-700 mb-1.5">관리자 휴대폰 번호</label>
                                            <div className="flex gap-2">
                                                <input
                                                    type="tel"
                                                    value={phoneNumber}
                                                    onChange={(e) => setPhoneNumber(e.target.value.replace(/[^0-9-]/g, ''))}
                                                    disabled={isPhoneVerified}
                                                    placeholder="번호만 입력해주세요"
                                                    className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3.5 outline-none focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all disabled:opacity-50 disabled:bg-gray-100"
                                                />
                                                <button
                                                    onClick={handleSendCode}
                                                    disabled={!phoneNumber || isPhoneVerified}
                                                    className="px-4 py-3.5 bg-gray-800 text-white rounded-xl font-semibold text-[14px] hover:bg-gray-900 transition-colors whitespace-nowrap disabled:bg-gray-200 disabled:text-gray-400"
                                                >
                                                    {isCodeSent ? '재전송' : '인증번호 받기'}
                                                </button>
                                            </div>
                                        </div>

                                        {isCodeSent && (
                                            <div className="animate-in fade-in slide-in-from-top-2 duration-300">
                                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">인증번호</label>
                                                <div className="flex gap-2">
                                                    <input
                                                        type="text"
                                                        maxLength={6}
                                                        value={verificationCode}
                                                        onChange={(e) => setVerificationCode(e.target.value.replace(/[^0-9]/g, ''))}
                                                        disabled={isPhoneVerified}
                                                        placeholder="인증번호 6자리"
                                                        className="w-full bg-gray-50 border border-gray-200 rounded-xl px-4 py-3.5 outline-none focus:bg-white focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all disabled:opacity-50 disabled:bg-gray-100"
                                                    />
                                                    <button
                                                        onClick={handleVerifyCode}
                                                        disabled={verificationCode.length !== 6 || isPhoneVerified}
                                                        className="px-6 py-3.5 bg-blue-600 text-white rounded-xl font-semibold text-[14px] hover:bg-blue-700 transition-colors whitespace-nowrap disabled:bg-gray-200 disabled:text-gray-400"
                                                    >
                                                        {isPhoneVerified ? '인증 완료' : '인증 확인'}
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                )
                        }

                        <button
                            onClick={nextStep}
                            disabled={!isPhoneVerified}
                            className="w-full mt-auto bg-blue-600 text-white rounded-xl py-4 font-semibold text-[16px] disabled:bg-gray-200 disabled:text-gray-400 hover:bg-blue-700 transition-colors"
                        >
                            다음으로
                        </button>
                    </div>
                )}

                {/* =================
                    STEP 5: 홈페이지 도메인

                    2026-10-30: 외부 쇼핑몰(카페24 등) 채널에서는 **선택 사항**이 됐다(`homepageStepMode`).
                    홈페이지는 오모플레이 제공 자사몰 고객에게 카카오 심사용 URL 을 주는 것이라, 자기 몰이 있는
                    고객에게는 쓰이지 않는 페이지·`hosting_info` 고아 행·OpenAI 배너 비용만 남긴다.
                    자동으로 건너뛰지 않고 **이유를 보여주고 작업자가 고른다** — 약관 호스팅을 오모플레이로 옮기는
                    경우(`_ai-runs/2026-08-11_002_cafe24_to_omoplay_hosting_migration/`)처럼 외부 몰 채널에도 만들 일이 있다.
                   ================= */}
                {step === 5 && (
                    <div className="flex flex-col flex-1 min-h-0">
                        <h2 className="text-2xl font-bold text-gray-900 mb-2 pr-20">홈페이지 도메인 생성</h2>
                        <p className="text-gray-500 mb-6 text-sm">
                            {homepageMode === HOMEPAGE_MODE_OPTIONAL
                                ? '이 채널은 외부 쇼핑몰 연동입니다. 오모플레이 홈페이지(자사몰)는 선택 사항입니다.'
                                : '홈페이지 도메인에 사용할 업체명을 입력해주세요.'}
                        </p>

                        <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar mb-6">
                            {homepageMode === HOMEPAGE_MODE_OPTIONAL && (
                                <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 p-4 text-[13px] leading-relaxed">
                                    <div className="flex items-start gap-2">
                                        <AlertCircle className="w-5 h-5 text-amber-500 shrink-0 mt-0.5" />
                                        <div>
                                            <p className="font-bold text-amber-800">
                                                연동 방식이 <strong>{hostingLabel}</strong> 로 기록된 채널입니다.
                                            </p>
                                            <p className="text-amber-700 mt-1">
                                                오모플레이 홈페이지는 오모플레이 제공 자사몰 고객에게 카카오 심사용 회원가입·약관·개인정보처리방침
                                                URL 을 주기 위한 것입니다. 고객사 쇼핑몰이 있는 채널에는 필요하지 않고, 만들면 사용되지 않는
                                                페이지와 AI 배너 생성 비용만 남습니다.
                                            </p>
                                            <p className="text-amber-700 mt-1">
                                                다음 단계(카카오 앱 설정)에서 앱 생성·심사 URL·서비스 약관 항목은
                                                <strong> 설정 불필요</strong>로 표시됩니다 — 쇼핑몰 카카오싱크 세팅이 자동으로 등록합니다.
                                                키 입력·리다이렉트 URI·웹훅만 진행하세요.
                                            </p>
                                        </div>
                                    </div>
                                    <div className="flex gap-2 mt-3">
                                        <button
                                            type="button"
                                            onClick={() => { setHomepageSkipped(true); setShowHomepageForm(false); }}
                                            className={`flex-1 py-2.5 rounded-lg text-[13px] font-semibold transition-colors ${
                                                homepageSkipped ? 'bg-gray-800 text-white' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                                            }`}
                                        >
                                            홈페이지 생성 없이 진행
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => { setShowHomepageForm(true); setHomepageSkipped(false); }}
                                            className={`flex-1 py-2.5 rounded-lg text-[13px] font-semibold transition-colors ${
                                                showHomepageForm ? 'bg-emerald-600 text-white' : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-50'
                                            }`}
                                        >
                                            그래도 홈페이지 만들기
                                        </button>
                                    </div>
                                </div>
                            )}

                            {homepageMode === HOMEPAGE_MODE_EXISTS && channelInfo?.isExternalMall && (
                                <p className="mb-3 text-[13px] text-gray-500">
                                    외부 쇼핑몰({hostingLabel}) 채널이지만 오모플레이 홈페이지가 이미 만들어져 있습니다.
                                </p>
                            )}

                            {(homepageMode !== HOMEPAGE_MODE_OPTIONAL || showHomepageForm) && (
                                <>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">업체 영문명</label>
                                <div className="flex items-center w-full bg-gray-50 border border-gray-200 rounded-xl px-4 focus-within:bg-white focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100 transition-all mb-4">
                                    <span className="text-gray-400 font-medium">https://</span>
                                    <input
                                        type="text"
                                        value={domainName}
                                        onChange={(e) => setDomainName(e.target.value.replace(/[^a-zA-Z0-9-]/g, ''))} // 영문,숫자,대시만
                                        disabled={isDomainCreated}
                                        maxLength={25}
                                        placeholder="dangol"
                                        className="w-full bg-transparent py-3.5 px-1 outline-none text-gray-900 font-medium disabled:opacity-50"
                                    />
                                    <span className="text-gray-400 font-medium whitespace-nowrap">.omoplay.com</span>
                                </div>
                                <label className="block text-sm font-semibold text-gray-700 mb-1.5">업체 한글명</label>
                                <div className="flex items-center w-full bg-gray-50 border border-gray-200 rounded-xl px-4 focus-within:bg-white focus-within:border-blue-500 focus-within:ring-2 focus-within:ring-blue-100 transition-all mb-4">
                                    <input
                                        type="text"
                                        value={brandName}
                                        onChange={(e) => setBrandName(e.target.value)}
                                        disabled={isDomainCreated}
                                        placeholder="업체(브랜드)명"
                                        className="w-full bg-transparent py-3.5 px-1 outline-none text-gray-900 font-medium disabled:opacity-50"
                                    />
                                </div>

                                {!isDomainCreated ? (
                                    <button
                                        onClick={handleCreateDomain}
                                        disabled={domainName.length < 3 || isBizChecking}
                                        className="w-full bg-gray-800 text-white rounded-xl py-3.5 font-semibold text-[15px] hover:bg-gray-900 transition-colors disabled:bg-gray-200 disabled:text-gray-400 flex justify-center"
                                    >
                                        {isBizChecking ? <Loader2 className="w-5 h-5 animate-spin" /> : '홈페이지 생성'}
                                    </button>
                                ) : (
                                    <div className="bg-indigo-50 border border-indigo-100 rounded-xl p-4 flex items-center justify-between animate-in fade-in">
                                        <div>
                                            <span className="text-xs font-bold text-indigo-500 block mb-0.5">생성 완료된 URL</span>
                                            <a href={generatedUrl} target="_blank" rel="noreferrer" className="text-[14px] text-indigo-900 font-medium hover:underline">
                                                {generatedUrl}
                                            </a>
                                        </div>
                                        <CheckCircle2 className="w-5 h-5 text-indigo-500" />
                                    </div>
                                )}
                                </>
                            )}
                        </div>

                        <button
                            onClick={nextStep}
                            disabled={!canLeaveHomepageStep}
                            className="w-full mt-auto bg-blue-600 text-white rounded-xl py-4 font-semibold text-[16px] disabled:bg-gray-200 disabled:text-gray-400 hover:bg-blue-700 transition-colors"
                        >
                            {homepageMode === HOMEPAGE_MODE_OPTIONAL && homepageSkipped && !isDomainCreated ? '홈페이지 생성 없이 다음으로' : '다음으로'}
                        </button>
                    </div>
                )}

                {/* =================
                    STEP 6: 카카오 디벨로퍼 앱 검증

                    2026-09-17 개편. 이 화면을 쓰는 사람은 절차를 이미 아는 실무자다.
                    필요한 건 '설명' 이 아니라 **카카오 화면에 그대로 붙여넣을 값**이다 ⇒
                    (1) 캡처는 기본으로 접고(`이미지로 확인 »`), (2) 복사 대상 값을 큰 복사 버튼과 함께 앞세우고,
                    (3) 카카오 디벨로퍼스의 **메뉴 이름 그대로** 묶어서 지금 보고 있는 화면과 대조되게 했다.
                   ================= */}
                {step === 6 && (
                    <div className="flex flex-col flex-1 min-h-0">
                        <div className="flex items-start justify-between gap-4 mb-3 shrink-0 pr-24">
                            <div className="min-w-0">
                                <h2 className="text-xl font-bold text-gray-900">카카오 디벨로퍼 앱 검증</h2>
                                <p className="text-gray-500 text-[13px] mt-0.5">
                                    아래 18가지 절차를 순서대로 진행해 주세요. 화면 캡처가 필요하면 각 항목의 <strong className="text-gray-700">이미지로 확인 »</strong> 를 누르면 펼쳐집니다.
                                </p>
                                {isMallChannel && (
                                    <p className="mt-1.5 text-[13px] text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-2.5 py-1.5">
                                        <strong>{hostingLabel} 연동 채널</strong> — 앱 생성·심사 URL·서비스 약관은 쇼핑몰 카카오싱크 세팅이 자동 등록합니다(<span className="font-semibold">설정 불필요</span> 표시).
                                        최종 연결된 디벨로퍼 앱의 <strong>REST API 키·JavaScript 키 입력, 리다이렉트 URI·웹훅 등록</strong>은 그대로 진행하세요.
                                    </p>
                                )}
                            </div>
                            <button
                                onClick={() => openLink('https://developers.kakao.com/console/app')}
                                className="shrink-0 flex items-center justify-center gap-2 text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 px-4 h-10 rounded-xl text-[13px] font-semibold transition-colors shadow-sm whitespace-nowrap"
                            >
                                <ExternalLink className="w-4 h-4 text-gray-400" />
                                카카오 디벨로퍼스 열기
                            </button>
                        </div>

                        {/*
                          스크롤 가능한 절차 영역. 넓은 화면에서는 2열로 펼쳐 한 화면에 담기는 항목 수를 늘린다.

                          `grid` 가 아니라 **CSS 다단(`columns`)** 이다. grid 는 행 단위로 맞추느라
                          짧은 묶음 아래에 빈 칸이 크게 남는데, 다단은 전체 높이를 반으로 갈라 채운다.
                          읽는 순서도 1→9(왼쪽) → 10→18(오른쪽) 으로 번호와 같은 방향이 된다.
                          묶음이 단 경계에서 잘리지 않도록 `GuideSection` 에 `break-inside-avoid` 를 준다.
                        */}
                        <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar border-t border-b border-gray-100 py-3 pr-1">
                            <div className="columns-1 xl:columns-2 gap-x-6">

                                <GuideSection title="앱 생성">
                                    <GuideItem num="1" text="디벨로퍼스 페이지에서 [+ 앱 생성] 버튼을 눌러 디벨로퍼 앱을 추가합니다. (로고 · 이름 · 카테고리 입력)" imgs={[Step1]}
                                               notNeeded={isMallChannel} notNeededNote="쇼핑몰 카카오싱크 자동화 도구가 앱 생성 또는 기존 앱 연결을 처리합니다." />
                                    <GuideItem num="2" text="앱 아이콘은 반드시 카카오 비즈니스센터와 동일하게 합니다. (비즈니스센터 아이콘 우클릭 → 다운로드 후 첨부)" imgs={[Step2]}
                                               notNeeded={isMallChannel} notNeededNote="앱 생성과 함께 처리됩니다." />
                                </GuideSection>

                                <GuideSection title="앱 > 일반">
                                    <GuideItem num="3" imgs={[Step3, Step4]}>
                                        <p>비즈니스 정보에 사업자등록번호를 입력합니다.</p>
                                        <CopyField label="사업자번호" value={bizRegNumber} />
                                    </GuideItem>
                                </GuideSection>

                                <GuideSection title="앱 > 추가 기능 신청">
                                    <GuideItem num="4" text="[신청 자격 확인] 버튼을 클릭합니다." imgs={[Step5, Step6]} />
                                    <GuideItem num="5" text="추가 기능 > [카카오톡 채널 연결] 을 신청하고 채널을 선택합니다." imgs={[Step7, Step8]} />
                                    <GuideItem num="6" imgs={[Step9, Step10]}>
                                        <p><strong className="text-red-500 font-bold">⚠️ 반드시 새로고침</strong> 한 뒤, 비즈니스 정보 &gt; [비즈니스 정보] 를 신청합니다.</p>
                                    </GuideItem>
                                    <GuideItem num="7" imgs={[Step11, Step12, Step14, Step13]}
                                               notNeeded={isMallChannel} notNeededNote="동의항목 신청과 심사용 회원가입·처리방침 URL 은 쇼핑몰 카카오싱크 세팅이 자동 등록합니다.">
                                        <p>추가 기능 &gt; [개인정보 동의항목] 을 신청합니다.</p>
                                        <p className="text-[12.5px] text-gray-500">필수 동의: 이름 · 성별 · 생일 · 출생연도 · 카카오계정(전화번호)</p>
                                        {/*
                                          🔴 회원가입 링크는 `?view=signup` 이어야 한다(2026-09-17 수정).
                                             종전에는 `/view/site` 만 복사돼 브랜드 홈이 열렸다. 카카오 심사에 첨부하는
                                             캡처는 **회원가입 화면** 이라, 실무자가 이 링크로 직접 들어가 화면을 찍는다
                                             ⇒ 복사 버튼 옆에 '새 창으로 링크 보기' 를 함께 둔다.
                                        */}
                                        <SiteUrlField label="회원가입" value={signupUrl} openUrl hostingLabel={hostingLabel} notNeeded={isMallChannel} />
                                        <SiteUrlField label="처리방침" value={privacyUrl} hostingLabel={hostingLabel} notNeeded={isMallChannel} />
                                        <CopyField label="수집 사유" value={PRIVACY_COLLECT_REASON} />
                                        <p className="text-[12.5px] text-gray-500">회원가입 화면 스크린샷을 첨부한 뒤 신청합니다.</p>
                                    </GuideItem>
                                </GuideSection>

                                <GuideSection title="앱 > 플랫폼 키 · REST API 키">
                                    <GuideItem num="8" imgs={[Step15]}>
                                        <p>Default REST API Key 를 선택합니다. <strong className="text-gray-900">REST API 키는 복사해 아래 입력칸에 붙여넣으세요.</strong></p>
                                        <p className="text-[12.5px] text-gray-500">카카오 로그인 리다이렉트 URI 를 추가합니다.</p>
                                        <CopyField label="리다이렉트 URI" value={KAKAO_REDIRECT_URI} />
                                    </GuideItem>
                                    <GuideItem num="9" text="클라이언트 시크릿 활성화를 OFF 로 둡니다." imgs={[Step34]} />
                                </GuideSection>

                                <GuideSection title="앱 > 플랫폼 키 · JavaScript 키">
                                    <GuideItem num="10" imgs={[Step16, Step17]}>
                                        <p>Default JS Key 를 선택합니다. <strong className="text-gray-900">JavaScript 키는 복사해 아래 입력칸에 붙여넣으세요.</strong></p>
                                        <CopyField label="JS SDK 도메인" value={KAKAO_JS_SDK_DOMAIN} />
                                        <CopyField label="리다이렉트 URI" value={KAKAO_REDIRECT_URI} />
                                    </GuideItem>
                                </GuideSection>

                                <GuideSection title="앱 > 웹훅">
                                    <GuideItem num="11" imgs={[Step18, Step19, Step20]}>
                                        <p>계정 상태 변경 웹훅을 등록합니다.</p>
                                        <CopyField label="웹훅 URL" value={KAKAO_WEBHOOK_ACCOUNT_STATUS} />
                                        <p className="text-[12.5px] text-gray-500">체크: 연결 / 해제, KAKAO 탭(프로필 정보 변경)</p>
                                    </GuideItem>
                                    <GuideItem num="12" imgs={[Step21]}>
                                        <p>카카오톡 채널 웹훅을 등록합니다.</p>
                                        <CopyField label="웹훅 URL" value={KAKAO_WEBHOOK_CHANNEL_FRIEND} />
                                    </GuideItem>
                                </GuideSection>

                                <GuideSection title="카카오 로그인 > 일반">
                                    <GuideItem num="13" text="카카오 로그인 사용 설정을 ON 으로 바꿉니다." imgs={[Step22]} />
                                </GuideSection>

                                <GuideSection title="카카오 로그인 > 간편가입">
                                    <GuideItem num="14" imgs={[Step23, Step24]} notNeeded={isMallChannel} notNeededNote="서비스 약관은 쇼핑몰 카카오싱크 세팅이 디벨로퍼 앱에 이미 등록합니다(카페24: https://{카페24 ID}.cafe24.com/api/member/agreement).">
                                        <p>서비스 약관 추가 ①</p>
                                        <CopyField label="한글" value="서비스 이용 약관" />
                                        <CopyField label="영문" value="Service terms" />
                                        <SiteUrlField label="URL" value={termsUrl} hostingLabel={hostingLabel} pageName="이용약관" notNeeded={isMallChannel} />
                                        <CopyField label="태그" value="terms" />
                                    </GuideItem>
                                    <GuideItem num="15" imgs={[Step25]} notNeeded={isMallChannel} notNeededNote="약관 ① 과 함께 쇼핑몰 세팅이 처리합니다.">
                                        <p>서비스 약관 추가 ② <span className="text-[12.5px] text-gray-500">(선택 동의)</span></p>
                                        <CopyField label="한글" value="SMS 수신 동의" />
                                        <CopyField label="영문" value="Agree to receive sms" />
                                        <CopyField label="태그" value="sms" />
                                    </GuideItem>
                                    <GuideItem num="16" imgs={[Step26]} notNeeded={isMallChannel} notNeededNote="약관 ① 과 함께 쇼핑몰 세팅이 처리합니다.">
                                        <p>서비스 약관 추가 ③ <span className="text-[12.5px] text-gray-500">(선택 동의)</span></p>
                                        <CopyField label="한글" value="이메일 수신 동의" />
                                        <CopyField label="영문" value="Agree to receive email" />
                                        <CopyField label="태그" value="email" />
                                    </GuideItem>
                                    <GuideItem
                                        num="17"
                                        text={isMallChannel
                                            ? '쇼핑몰 세팅이 등록한 약관이 모두 ON 이고 간편가입 사용 설정이 ON 인지 확인만 합니다.'
                                            : '약관 ①·②·③ 을 모두 ON 으로 바꾼 뒤, 간편가입 사용 설정을 ON 으로 바꿉니다.'}
                                        imgs={[Step27, Step28]}
                                    />
                                </GuideSection>

                                <GuideSection title="카카오 로그인 > 동의항목">
                                    <GuideItem num="18" text="대표 채널을 선택합니다." imgs={[Step29]} />
                                </GuideSection>

                            </div>
                        </div>

                        {/* 카카오 디벨로퍼스에서 복사해 온 키 두 개. 위 8·10번 항목이 가리키는 입력칸이다. */}
                        <div className="shrink-0 pt-3">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                                <div>
                                    <label className="block text-[12px] font-bold text-gray-500 mb-1">REST API 키 <span className="font-normal text-gray-400">(8번에서 복사)</span></label>
                                    <input
                                        type="text"
                                        value={restApiKey}
                                        onChange={(e) => setRestApiKey(e.target.value)}
                                        placeholder="REST API 키를 붙여넣어 주세요"
                                        className="w-full border border-gray-200 bg-gray-50 rounded-lg px-3 py-2.5 text-sm font-mono outline-none focus:bg-white focus:border-blue-500"
                                    />
                                </div>
                                <div>
                                    <label className="block text-[12px] font-bold text-gray-500 mb-1">JavaScript 키 <span className="font-normal text-gray-400">(10번에서 복사)</span></label>
                                    <input
                                        type="text"
                                        value={jsKey}
                                        onChange={(e) => setJsKey(e.target.value)}
                                        placeholder="JavaScript 키를 붙여넣어 주세요"
                                        className="w-full border border-gray-200 bg-gray-50 rounded-lg px-3 py-2.5 text-sm font-mono outline-none focus:bg-white focus:border-blue-500"
                                    />
                                </div>
                            </div>
                            <button
                                onClick={() => {
                                    setupChannel(selectedBiz.channelId, restApiKey, jsKey, selectedAccount).then(() => {
                                        nextStep()
                                    }).catch(() => alert("저장에 실패했습니다. 채널이 존재하지 않습니다."))
                                }}
                                disabled={!(restApiKey.length > 30) || !(jsKey.length > 30)}
                                className="w-full bg-blue-600 text-white rounded-xl py-3.5 font-semibold text-[16px] disabled:bg-gray-200 disabled:text-gray-400 hover:bg-blue-700 transition-colors"
                            >
                                18단계 검증을 모두 완료했습니다
                            </button>
                        </div>
                    </div>
                )}

                {/* ================= STEP 7: 싱크 테스트 ================= */}
                {
                    step === 7 && (
                        <div className="flex flex-col flex-1 min-h-0">
                            <h2 className="text-2xl font-bold text-gray-900 mb-2 pr-20">카카오 싱크 적용 테스트</h2>
                            <button
                                onClick={prevStep}
                                className="w-full mt-4 shrink-0 bg-gray-200 rounded-xl py-4 font-semibold text-[16px] hover:bg-gray-400 transition-colors mb-4"
                            >
                                이전으로
                            </button>
                            <button
                                onClick={() => openLink(`/sysadmin/wizard/login-test?jsKey=${jsKey}&resKey=${restApiKey}`)}
                                className="w-full mt-auto bg-yellow-400 text-white rounded-xl py-4 font-semibold text-[16px] hover:bg-yellow-500 transition-colors mb-4"
                            >
                                카카오 싱크 적용 테스트
                            </button>
                            <button
                                onClick={nextStep}
                                className="w-full mt-auto bg-blue-600 text-white rounded-xl py-4 font-semibold text-[16px] hover:bg-blue-700 transition-colors"
                            >
                                카카오 싱크가 정상적으로 동작합니다
                            </button>
                        </div>
                    )
                }

                {/* ================= STEP 8: 심사 승인 후 설정 ================= */}
                {step === 8 && (
                    <div className="flex flex-col flex-1 min-h-0">
                        <div className="mb-3 shrink-0 pr-24">
                            <h2 className="text-xl font-bold text-gray-900">심사 승인 후 설정 추가</h2>
                            <p className="text-gray-500 text-[13px] mt-0.5">앱 심사가 승인된 이후 아래 3가지 설정을 마무리해 주세요.</p>
                        </div>

                        <div className="flex-1 min-h-0 overflow-y-auto no-scrollbar border-t border-b border-gray-100 py-3 pr-1">
                            <div className="columns-1 xl:columns-2 gap-x-6">

                                <GuideSection title="카카오 로그인 > 동의항목">
                                    <GuideItem num="1" imgs={[Step30, Step31]}>
                                        <p>아래 항목을 모두 <strong className="text-gray-900">필수 동의</strong>로 변경합니다.</p>
                                        <p className="text-[12.5px] text-gray-500">닉네임 · 프로필 사진 · 이메일 · 이름 · 성별 · 생일 · 출생연도 · 전화번호</p>
                                        <p>변경 시 수집 체크 및 동의 목적을 기재합니다.</p>
                                        <CopyField label="동의 목적" value="회원가입 정보 확인" />
                                    </GuideItem>
                                </GuideSection>

                                <GuideSection title="앱 > 멤버">
                                    <GuideItem num="2" text="업체 이메일로 멤버를 추가한 뒤, 멤버 수정에서 권한을 Editor → Owner 로 변경합니다." imgs={[Step32]} />
                                </GuideSection>

                                <GuideSection title="채널 > 관리자">
                                    <GuideItem num="3" imgs={[Step33]}>
                                        <p>아래 계정을 관리자로 초대한 뒤 채널에서 나갑니다.</p>
                                        <CopyField label="관리자" value={CHANNEL_ADMIN_EMAIL} />
                                    </GuideItem>
                                </GuideSection>

                            </div>
                        </div>

                        <div className="shrink-0 pt-3 flex gap-3">
                            <button
                                onClick={prevStep}
                                className="w-48 shrink-0 bg-gray-200 rounded-xl py-3.5 font-semibold text-[16px] hover:bg-gray-300 transition-colors"
                            >
                                이전으로
                            </button>
                            <button
                                onClick={() => {
                                    completed(selectedBiz.channelId).then(() => {
                                        nextStep()
                                    }).catch(() => alert("저장에 실패했습니다. 채널이 존재하지 않습니다."))
                                }}
                                className="flex-1 bg-blue-600 text-white rounded-xl py-3.5 font-semibold text-[16px] hover:bg-blue-700 transition-colors"
                            >
                                모든 설정을 완료했습니다
                            </button>
                        </div>
                    </div>
                )}

                {step === 9 && (
                    <div className="flex flex-col items-center justify-center flex-1 min-h-0 py-8 text-center">
                        <div className="w-20 h-20 bg-blue-500 rounded-full flex items-center justify-center mb-6 shadow-lg shadow-blue-500/30">
                            <Check className="w-10 h-10 text-white stroke-[3]" />
                        </div>
                        <h2 className="text-3xl font-bold text-gray-900 mb-3 tracking-tight">세팅이 모두 완료되었어요!</h2>
                        <p className="text-gray-500 mb-10 text-sm">관리자용 카카오 채널 및 디벨로퍼 앱 세팅이 성공적으로 마무리되었습니다.</p>
                        <button onClick={() => window.location.href = '/'} className="w-full bg-gray-900 text-white rounded-xl py-4 font-semibold text-[16px] hover:bg-black transition-colors">리스트로 돌아가기</button>
                    </div>
                )}
            </div>
        </div>
        </CopiedContext.Provider>
    );
}

/**
 * 값 하나를 클립보드로 복사하는 버튼.
 *
 * 실무자가 한 채널을 세팅하는 동안 수십 번 누르는 버튼이라 **크게**(h-9, 아이콘+글자) 만들었다.
 * 종전에는 아이콘만 있는 24px 버튼이어서 눌렸는지 눈으로 알 수 없었고, 확인 삼아 같은 값을
 * 두세 번 복사하는 일이 있었다 ⇒ 성공하면 **초록 체크 + '복사됨'** 으로 1.6초간 바뀐다.
 *
 * 🔴 성공했을 때만 초록으로 바꾼다. `navigator.clipboard` 는 보안 컨텍스트(https·localhost)에서만
 *    동작하므로 실패할 수 있는데, 실패를 성공처럼 보여주면 붙여넣기 전까지 아무도 모른다.
 *    실패는 빨간 '복사 실패' 로 남겨서 값을 직접 긁어 가도록 한다.
 */
function CopyButton({ value, label = '복사', className = '', onCopied }) {
    /** `idle` | `copied` | `failed` */
    const [state, setState] = useState('idle');

    useEffect(() => {
        if (state === 'idle') return;
        const timer = setTimeout(() => setState('idle'), 1600);
        return () => clearTimeout(timer);
    }, [state]);

    const handleCopy = async () => {
        try {
            if (!navigator.clipboard?.writeText) throw new Error('clipboard unavailable');
            await navigator.clipboard.writeText(String(value));
            setState('copied');
            onCopied?.();
        } catch {
            setState('failed');
        }
    };

    const tone = state === 'copied'
        ? 'bg-emerald-50 border-emerald-400 text-emerald-700'
        : state === 'failed'
            ? 'bg-red-50 border-red-300 text-red-600'
            : 'bg-white border-gray-300 text-gray-600 hover:bg-gray-50 hover:text-gray-900 hover:border-gray-400';

    return (
        <button
            type="button"
            onClick={handleCopy}
            title="클립보드에 복사"
            className={`shrink-0 inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border font-semibold text-[13px] transition-all active:scale-95 ${tone} ${className}`}
        >
            {state === 'copied'
                ? <Check className="w-4 h-4 stroke-[3]" />
                : state === 'failed'
                    ? <AlertCircle className="w-4 h-4" />
                    : <Copy className="w-4 h-4" />}
            {state === 'copied' ? '복사됨' : state === 'failed' ? '복사 실패' : label}
        </button>
    );
}

/**
 * '이 값을 그대로 카카오 화면에 붙여넣는다' 를 한 줄로 보여주는 행.
 *
 * `openUrl` 을 주면 복사 버튼 왼쪽에 **새 창으로 열기** 아이콘이 붙는다. 회원가입 링크처럼
 * 실무자가 그 페이지에 직접 들어가 화면을 캡처해야 하는 값에 쓴다.
 */
export function CopyField({ label, value, openUrl = false }) {
    const missing = !value;
    const {copiedKeys, markCopied} = useContext(CopiedContext);
    const itemNum = useContext(GuideItemContext);
    const copiedKey = copiedKeyOf(itemNum, label, value);
    /** 이 세션에서 한 번 이상 복사에 성공했다 → 파란 상자(한 것). 아니면 회색(할 것). */
    const copied = !missing && copiedKeys.has(copiedKey);

    /*
      라벨은 상자 **밖** 왼쪽에 둔다(2026-10-30 피드백). 라벨·값·버튼이 한 테두리 안에 있으면
      "웹훅 URL https://…" 처럼 라벨이 값의 일부로 읽혀 가독성이 떨어졌다. 상자 안에는 값과 복사 버튼만.
    */
    return (
        <div className="flex items-center gap-2" data-copied={copied ? 'true' : 'false'}>
            {label && (
                <span className={`shrink-0 min-w-[4.5rem] text-[12px] font-bold leading-tight ${copied ? 'text-blue-700' : 'text-gray-500'}`}>
                    {label}
                </span>
            )}
            <div className={`flex-1 min-w-0 flex items-center gap-2 rounded-lg border px-2.5 py-1.5 transition-colors ${
                copied ? 'border-blue-400 bg-blue-50 ring-1 ring-blue-200' : 'border-gray-200 bg-gray-50'
            }`}>
            {copied && <Check className="w-4 h-4 shrink-0 text-blue-600 stroke-[3]" aria-label="복사 완료" />}
            <code className={`flex-1 min-w-0 truncate text-[13px] font-mono ${copied ? 'text-blue-900' : 'text-gray-900'}`} title={missing ? '' : String(value)}>
                {missing ? <span className="text-red-500 font-sans font-semibold">값 없음 — 앞 단계를 먼저 완료하세요</span> : value}
            </code>
            {openUrl && !missing && (
                <button
                    type="button"
                    onClick={() => window.open(String(value), '_blank', 'noopener,noreferrer')}
                    title="새 창으로 링크 보기"
                    className="shrink-0 inline-flex items-center justify-center h-9 w-9 rounded-lg border border-gray-300 bg-white text-gray-600 hover:bg-gray-50 hover:text-gray-900 hover:border-gray-400 active:scale-95 transition-all"
                >
                    <ExternalLink className="w-4 h-4" />
                </button>
            )}
            {!missing && <CopyButton value={value} label={copied ? '다시 복사' : '복사'} onCopied={() => markCopied(copiedKey)} />}
            </div>
        </div>
    );
}

/**
 * 오모플레이 홈페이지(자사몰) URL 칸. 홈페이지가 **없는** 채널(외부 몰, STEP 5 건너뜀)에서는
 * 값 대신 "고객사 쇼핑몰의 해당 페이지 URL 을 쓰라" 는 안내를 보여준다(2026-10-30).
 *
 * `CopyField` 의 기본 결손 문구("앞 단계를 먼저 완료하세요")는 이 경우 틀린 안내다 — 앞 단계는 의도적으로 건너뛴 것이다.
 */
function SiteUrlField({ label, value, openUrl = false, hostingLabel, pageName, notNeeded = false }) {
    /*
      외부 쇼핑몰(카페24) 채널: 심사 URL·약관 URL 은 쇼핑몰 카카오싱크 세팅이 자동 등록한다(사용자 확인 2026-10-30).
      값이 있어도(홈페이지를 굳이 만든 경우) 넣지 말라는 뜻이라 **잠근 행**으로 보여준다.
    */
    if (notNeeded) {
        return (
            <div className="flex items-center gap-2" aria-disabled="true">
                {label && <span className="shrink-0 min-w-[4.5rem] text-[12px] font-bold text-gray-400 leading-tight">{label}</span>}
                <span className="flex-1 min-w-0 rounded-lg border border-gray-200 bg-gray-100 px-2.5 py-1.5 text-[12.5px] text-gray-500 opacity-70">
                    <strong className="text-gray-600">설정 불필요</strong> — 쇼핑몰({hostingLabel ?? '외부 몰'}) 카카오싱크 세팅이 자동 등록합니다.
                </span>
            </div>
        );
    }
    if (value) return <CopyField label={label} value={value} openUrl={openUrl} />;

    return (
        <div className="flex items-center gap-2">
            {label && <span className="shrink-0 min-w-[4.5rem] text-[12px] font-bold text-gray-500 leading-tight">{label}</span>}
            <span className="flex-1 min-w-0 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-1.5 text-[12.5px] text-amber-800">
                오모플레이 홈페이지를 만들지 않은 채널입니다 — 고객사 쇼핑몰({hostingLabel ?? '외부 몰'})의 <strong>{pageName ?? label}</strong> 페이지 URL 을 사용하세요.
            </span>
        </div>
    );
}

/** 카카오 디벨로퍼스의 화면(메뉴) 단위 묶음. 실무자가 보고 있는 화면 이름과 제목을 맞춘다. */
function GuideSection({ title, children }) {
    return (
        <section className="mb-4 break-inside-avoid">
            <h3 className="mb-1.5 text-[12px] font-bold text-gray-400 tracking-wide">{title}</h3>
            <div className="space-y-2">{children}</div>
        </section>
    );
}

/**
 * 절차 한 항목.
 *
 * **이미지는 기본으로 접혀 있다**(2026-09-17). 이 화면을 쓰는 사람은 절차를 이미 숙지한 실무자라
 * 캡처가 항상 펼쳐져 있으면 한 화면에 두세 항목밖에 들어가지 않았다. 정작 중요한 '복사할 값' 이
 * 스크롤 뒤로 밀린다 ⇒ 캡처는 `이미지로 확인 »` 를 눌렀을 때만 펼친다.
 */
function GuideItem({ num, text, imgs, children, notNeeded = false, notNeededNote }) {
    const [showImages, setShowImages] = useState(false);
    const [previewImage, setPreviewImage] = useState();
    const hasImages = (imgs?.length ?? 0) > 0;

    /*
      `notNeeded`(2026-10-30): 외부 쇼핑몰(카페24) 채널에서 쇼핑몰 카카오싱크 세팅이 대신 처리하는 항목.
      항목을 **지우지 않고 회색으로 잠근다** — 작업자가 "이 번호가 왜 비었나" 를 묻지 않게 번호와 이유를 남긴다.
    */
    return (
        <GuideItemContext.Provider value={num}>
        <div className={`rounded-xl border px-3 py-2.5 shadow-sm ${notNeeded ? 'border-gray-200 bg-gray-50' : 'border-gray-200 bg-white'}`}>
            <div className="flex items-start gap-2.5">
                <div className={`w-6 h-6 shrink-0 font-bold text-[13px] rounded-full flex items-center justify-center mt-0.5 ${
                    notNeeded ? 'bg-gray-200 text-gray-500' : 'bg-blue-100 text-blue-600'}`}>
                    {num}
                </div>
                <div className={`min-w-0 flex-1 text-[13.5px] leading-relaxed space-y-1.5 ${notNeeded ? 'text-gray-400' : 'text-gray-700'}`}>
                    {notNeeded && (
                        <p className="text-[12.5px]">
                            <span className="inline-block rounded px-1.5 py-0.5 bg-gray-200 text-gray-700 font-bold mr-1.5">설정 불필요</span>
                            <span className="text-gray-600">{notNeededNote}</span>
                        </p>
                    )}
                    {text && <p className={notNeeded ? 'line-through' : ''}>{text}</p>}
                    {children}
                </div>
                {hasImages && (
                    <button
                        type="button"
                        onClick={() => setShowImages((prev) => !prev)}
                        className={`shrink-0 inline-flex items-center gap-1 h-8 px-2.5 rounded-lg border text-[12px] font-semibold whitespace-nowrap transition-colors ${
                            showImages
                                ? 'bg-blue-50 border-blue-200 text-blue-700'
                                : 'bg-white border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-800 hover:border-gray-300'
                        }`}
                    >
                        <ImageIcon className="w-3.5 h-3.5" />
                        {showImages ? '이미지 접기' : '이미지로 확인 »'}
                    </button>
                )}
            </div>

            {hasImages && showImages && (
                <div className={`mt-2.5 grid gap-2 ${imgs.length > 1 ? 'grid-cols-2' : 'grid-cols-1'}`}>
                    {imgs.map((img, index) => (
                        <div
                            key={`${num}-${index}`}
                            onClick={() => setPreviewImage(img)}
                            className="relative group rounded-lg overflow-hidden border border-gray-200 cursor-pointer shadow-sm hover:shadow-md transition-all"
                        >
                            <img
                                src={img}
                                alt={`${num}번 절차 참고 이미지 ${index + 1}`}
                                className="w-full transition-transform duration-300 group-hover:scale-105"
                            />
                            {/* 돋보기 오버레이 */}
                            <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                                <ZoomIn className="w-7 h-7 text-white opacity-0 group-hover:opacity-100 transition-opacity drop-shadow-md" />
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {/* 이미지 크게 보기 팝업 (모달) */}
            {previewImage && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200"
                    onClick={() => setPreviewImage(null)} // 배경 클릭 시 닫기
                >
                    <div
                        className="bg-white rounded-2xl shadow-2xl overflow-hidden max-w-5xl w-full flex flex-col animate-in zoom-in-95 duration-200"
                        onClick={(e) => e.stopPropagation()} // 모달 내부 클릭 시 닫히지 않도록 방지
                    >
                        {/* 팝업 헤더 */}
                        <div className="flex justify-between items-center px-4 py-3 border-b">
                            <h3 className="text-[15px] font-bold text-gray-900 flex items-center gap-2">
                                <ImageIcon className="w-5 h-5 text-gray-500" />
                                {num}번 절차 참고 이미지
                            </h3>
                            <button
                                onClick={() => setPreviewImage(null)}
                                className="p-1 hover:bg-gray-100 rounded-full transition-colors text-gray-500"
                            >
                                <X className="w-6 h-6" />
                            </button>
                        </div>

                        {/* 팝업 콘텐츠 (큰 이미지) */}
                        <div className="p-4 bg-gray-50 flex justify-center items-center h-[72vh]">
                            <img
                                src={previewImage}
                                alt="preview-zoom"
                                className="max-w-full max-h-full object-contain rounded-lg shadow-sm"
                            />
                        </div>
                    </div>
                </div>
            )}
        </div>
        </GuideItemContext.Provider>
    );
}
